import { PHASE, PLAYER } from "../../common/constants.ts";
import { uniformSeed } from "../../common/random.ts";
import { contractAmountForYears } from "../../common/contractYearAmount.ts";
import { player, team } from "../core/index.ts";
import moodComponents from "../core/player/moodComponents.ts";
import { idb } from "../db/index.ts";
import { g, helpers, logEvent } from "../util/index.ts";
import {
	getClient,
	getLeagueIdForLid,
	getLobbyState,
	getSession,
} from "./index.ts";
import { syncNow } from "./sync.ts";

// ============================================================================
// Phase 5: blind-bid free agency. Humans secretly bid per round; the runner's
// client resolves each round deterministically with BBGM's player-interest
// logic and commits the signings as an event everyone applies.
// ============================================================================

export type FaBid = {
	league_id: string;
	round: number;
	tid: number;
	pid: number;
	user_id: string;
	amount: number; // thousands, like BBGM contracts
	years: number;
	created_at: string;
};

export type FaSigning = {
	pid: number;
	tid: number;
	amount: number;
	exp: number;
	name: string;
};

export type FaRoundEventPayload = {
	round: number;
	phase: number;
	season: number;
	signings: FaSigning[];
	final: boolean;
};

export const placeBid = async (
	leagueId: string,
	pid: number,
	amount: number,
	years: number,
) => {
	// Clamp to the league's current min/max contract (era-correct - maxContract
	// tracks the real historical values via scheduled events, e.g. tiny in 1980
	// and growing over the years). Bidding above the max is never useful: the
	// resolution bounds the signing at maxContract anyway, so an over-max bid
	// only misleads the bidder about what they're committing.
	const clamped = helpers.bound(
		helpers.roundContract(amount),
		g.get("minContract"),
		g.get("maxContract"),
	);
	const years2 = helpers.bound(
		Math.round(years),
		g.get("minContractLength"),
		g.get("maxContractLength"),
	);
	const { data, error } = await getClient().rpc("place_fa_bid", {
		p_league_id: leagueId,
		p_pid: pid,
		p_amount: clamped,
		p_years: years2,
	});
	if (error) {
		throw new Error(error.message);
	}
	return data as { status: string };
};

export const withdrawBid = async (leagueId: string, pid: number) => {
	const { error } = await getClient().rpc("withdraw_fa_bid", {
		p_league_id: leagueId,
		p_pid: pid,
	});
	if (error) {
		throw new Error(error.message);
	}
};

export type SigningEventPayload = {
	pid: number;
	tid: number;
	amount: number;
	exp: number;
	phase: number;
	season: number;
	name: string;
};

// Sign a free agent directly (first-come-first-serve, like normal BBGM) -
// outside the blind-bid auction, at the player's asking price for the chosen
// number of years. Cap-checked like a normal signing. The RPC atomically
// claims the player in the ownership ledger, so if another human beat you to
// them it returns already_signed and nobody double-signs.
export const signFreeAgent = async (
	leagueId: string,
	pid: number,
	years: number,
): Promise<{ status: string }> => {
	const p = await idb.cache.players.get(pid);
	if (!p || p.tid !== PLAYER.FREE_AGENT) {
		return { status: "not_a_free_agent" };
	}

	const userTid = g.get("userTid");
	const roster = await idb.cache.players.indexGetAll("playersByTid", userTid);
	if (roster.length >= g.get("maxRosterSize")) {
		return { status: "roster_full" };
	}

	// Vanilla runs every free agent signing through contractNegotiation, which
	// refuses outright when the player isn't willing to play for you, and asks
	// for the mood-adjusted price (different for every team). Do the same here
	// so a direct signing charges what the Free Agents page displayed instead
	// of the player's raw asking number.
	const mood = await player.moodInfo(p, userTid);
	if (!mood.willing) {
		return { status: "refuses" };
	}

	const minContract = g.get("minContract");
	const boundedYears = helpers.bound(
		Math.round(years),
		g.get("minContractLength"),
		g.get("maxContractLength"),
	);
	// ...and more per year the further the offered length is from the one he
	// wants, exactly like vanilla's contract options.
	const amount = contractAmountForYears({
		baseAmount: mood.contractAmount,
		desiredExp: p.contract.exp,
		ovr: p.ratings.at(-1)!.ovr,
		years: boundedYears,
		season: g.get("season"),
		minContractLength: g.get("minContractLength"),
		maxContractLength: g.get("maxContractLength"),
		minContract,
		maxContract: g.get("maxContract"),
	});

	// Soft-cap check, same as the auction: min-contract signings are always
	// allowed, otherwise the payroll must fit under the cap.
	const payroll = await team.getPayroll(userTid);
	const capLegal =
		amount - 1 <= minContract || payroll + amount - 1 <= g.get("salaryCap");
	if (!capLegal) {
		return { status: "over_cap" };
	}

	const event: SigningEventPayload = {
		pid,
		tid: userTid,
		amount,
		exp: g.get("season") + boundedYears,
		phase: g.get("phase"),
		season: g.get("season"),
		name: `${p.firstName} ${p.lastName}`,
	};

	const { data, error } = await getClient().rpc("sign_free_agent", {
		p_league_id: leagueId,
		p_pid: pid,
		p_event: event,
		p_tid: userTid,
	});
	if (error) {
		throw new Error(error.message);
	}
	if (data.status !== "signed") {
		return data;
	}

	await syncNow(leagueId);
	return { status: "signed" };
};

export const currentRoundBids = async (
	leagueId: string,
	round: number,
): Promise<FaBid[]> => {
	const { data, error } = await getClient()
		.from("fa_bids")
		.select()
		.eq("league_id", leagueId)
		.eq("round", round);
	if (error) {
		throw new Error(error.message);
	}
	return (data ?? []) as FaBid[];
};

// ============================================================================
// Willingness + demand: a copy of core/player/moodInfo.ts's math with one
// addition - the faWinnerDampen knob scales down the team-performance
// component so championship teams can't hoard every free agent. Deterministic
// (moodInfo already uses a seeded random).
// ============================================================================

const moodWillingAndDemand = async (
	p: any,
	tid: number,
	dampen: number,
): Promise<{ willing: boolean; demand: number }> => {
	const components = await moodComponents(p, tid);

	if (dampen > 0 && components.teamPerformance > 0) {
		components.teamPerformance *= 1 - dampen;
	}

	let sumComponents = 0;
	for (const key of helpers.keys(components)) {
		if (key === "custom") {
			for (const row of components.custom!) {
				sumComponents += row.amount;
			}
		} else {
			sumComponents += components[key] as number;
		}
	}

	let sumAndStuff = sumComponents - 0.5;
	if (p.tid === PLAYER.FREE_AGENT) {
		sumAndStuff += helpers.bound(p.numDaysFreeAgent, 0, 30) / 3;
	}

	let valueDiff = (p.value - 65) / 2;
	// (The re-signing cap on valueDiff never applies in the auction: these are
	// free agents with no active negotiation)
	sumAndStuff -= valueDiff > 0 ? Math.sqrt(valueDiff) : valueDiff;

	const thisIsAUserTeam = g.get("userTids").includes(tid);
	if (!thisIsAUserTeam) {
		sumAndStuff -= 3;
	}

	let contractAmount = p.contract.amount;
	const autoRookieContract =
		components.rookieContract > 0 && g.get("draftPickAutoContract");
	if (!autoRookieContract && contractAmount > g.get("minContract")) {
		contractAmount *= helpers.bound(1 + (0.5 * -sumComponents) / 10, 1, 1.5);
	}
	contractAmount = helpers.bound(
		helpers.roundContract(contractAmount),
		g.get("minContract"),
		g.get("maxContract"),
	);

	const phase = g.get("phase");
	const season = g.get("season");
	const rookie =
		phase >= PHASE.DRAFT &&
		phase <= PHASE.RESIGN_PLAYERS &&
		p.draft.year === season;

	let willing = false;
	if (
		!g.get("playersRefuseToNegotiate") ||
		rookie ||
		(contractAmount === g.get("minContract") && p.tid === PLAYER.FREE_AGENT)
	) {
		willing = true;
	} else if (components.rookieContract > 0 && !g.get("rookiesCanRefuse")) {
		willing = true;
	} else {
		const probWilling = 1 / (1 + Math.exp(-0.7 * sumAndStuff));
		const seed =
			tid +
			p.pid +
			p.stats.length +
			p.ratings.at(-1)!.ovr +
			(p.stats.at(-1)?.min ?? 0);
		willing = uniformSeed(seed) < probWilling;
	}

	if (
		g.get("challengeNoFreeAgents") &&
		contractAmount * 0.99 > g.get("minContract")
	) {
		willing = false;
	}

	return { willing, demand: contractAmount };
};

// ============================================================================
// Round resolution (runner only)
// ============================================================================

type ResolutionDetail = {
	pid: number;
	name: string;
	outcome: "signed" | "refused" | "over_cap" | "outbid" | "ai_signed";
	tid: number;
	amount: number;
};

export const resolveFaRound = async (
	leagueId: string,
): Promise<{
	status: string;
	signings?: number;
	final?: boolean;
	details?: ResolutionDetail[];
}> => {
	const { league: mpLeague } = await getLobbyState(leagueId);
	const session = await getSession();
	if (
		!session ||
		session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
	) {
		throw new Error("Only the sim runner can resolve a free agency round");
	}
	if (!mpLeague.fa_open) {
		return { status: "fa_not_open" };
	}

	const round = mpLeague.fa_round;
	const bids = await currentRoundBids(leagueId, round);
	const dampen = helpers.bound(mpLeague.settings.faWinnerDampen ?? 0, 0, 1);

	const signings: FaSigning[] = [];
	const details: ResolutionDetail[] = [];
	// Free agency is a single blind-bid round: winners sign, losers stay free
	// agents (rosters get topped up like vanilla BBGM - AI fills to the minimum
	// during sims, and unsigned players remain available all season).
	const final = true;

	if (bids.length > 0) {
		// Deterministic resolution order: best players first
		const pids = [...new Set(bids.map((b) => b.pid))];
		const players: any[] = [];
		for (const pid of pids) {
			const p = await idb.cache.players.get(pid);
			if (p && p.tid === PLAYER.FREE_AGENT) {
				players.push(p);
			}
		}
		players.sort((x, y) => y.value - x.value || x.pid - y.pid);

		// Cap accounting includes wins from earlier in this same round
		const payrolls = new Map<number, number>();
		const getTeamPayroll = async (tid: number) => {
			if (!payrolls.has(tid)) {
				payrolls.set(tid, await team.getPayroll(tid));
			}
			return payrolls.get(tid)!;
		};

		const salaryCap = g.get("salaryCap");
		const minContract = g.get("minContract");
		const maxContract = g.get("maxContract");
		const season = g.get("season");

		// Anti-lowball: AI teams that can scoop a player nobody bid enough for.
		// Bookkeeping (payroll via getTeamPayroll, roster count here) updates as
		// they sign, so one AI team can't sign past its cap/roster limit.
		const userTids = g.get("userTids");
		const aiTeams = (await idb.cache.teams.getAll()).filter(
			(t) => !t.disabled && !userTids.includes(t.tid),
		);
		const maxRosterSize = g.get("maxRosterSize");
		const aiRosterCount = new Map<number, number>();
		for (const t of aiTeams) {
			aiRosterCount.set(
				t.tid,
				(await idb.cache.players.indexGetAll("playersByTid", t.tid)).length,
			);
		}

		for (const p of players) {
			const playerBids = bids
				.filter((b) => b.pid === p.pid)
				.sort((x, y) => y.amount - x.amount || x.tid - y.tid);

			let signed = false;
			for (const bid of playerBids) {
				const payroll = await getTeamPayroll(bid.tid);
				const capLegal =
					bid.amount - 1 <= minContract ||
					payroll + bid.amount - 1 <= salaryCap;
				if (!capLegal) {
					details.push({
						pid: p.pid,
						name: `${p.firstName} ${p.lastName}`,
						outcome: "over_cap",
						tid: bid.tid,
						amount: bid.amount,
					});
					continue;
				}

				const { willing, demand } = await moodWillingAndDemand(
					p,
					bid.tid,
					dampen,
				);
				// The player wants more per year the further the offered length is
				// from his preferred one, exactly like vanilla's contract options.
				const demandForYears = contractAmountForYears({
					baseAmount: demand,
					desiredExp: p.contract.exp,
					ovr: p.ratings.at(-1)!.ovr,
					years: bid.years,
					season,
					minContractLength: g.get("minContractLength"),
					maxContractLength: g.get("maxContractLength"),
					minContract,
					maxContract,
				});
				if (!willing || bid.amount < demandForYears) {
					details.push({
						pid: p.pid,
						name: `${p.firstName} ${p.lastName}`,
						outcome: "refused",
						tid: bid.tid,
						amount: bid.amount,
					});
					continue;
				}

				const years = helpers.bound(
					bid.years,
					g.get("minContractLength"),
					g.get("maxContractLength"),
				);
				signings.push({
					pid: p.pid,
					tid: bid.tid,
					amount: helpers.bound(
						helpers.roundContract(bid.amount),
						minContract,
						g.get("maxContract"),
					),
					exp: season + years,
					name: `${p.firstName} ${p.lastName}`,
				});
				details.push({
					pid: p.pid,
					name: `${p.firstName} ${p.lastName}`,
					outcome: "signed",
					tid: bid.tid,
					amount: bid.amount,
				});
				payrolls.set(bid.tid, payroll + bid.amount);
				signed = true;
				break;
			}

			if (!signed && playerBids.length > 0) {
				// Anti-lowball: every human bid on this player, but none met his
				// asking. Rather than let a lowballer grab him cheap next round, a
				// cap-legal AI team with roster room signs him at his asking (only
				// worth doing for players who want more than the minimum - nobody
				// lowballs a min-salary scrub). Deterministic: pick the eligible AI
				// team with the most cap space (ties -> lowest tid).
				const askingAmount = helpers.bound(
					helpers.roundContract(p.contract.amount),
					minContract,
					maxContract,
				);
				if (askingAmount > minContract) {
					let bestTid: number | undefined;
					let bestSpace = -Infinity;
					for (const t of aiTeams) {
						if ((aiRosterCount.get(t.tid) ?? maxRosterSize) >= maxRosterSize) {
							continue;
						}
						const payroll = await getTeamPayroll(t.tid);
						const capLegal =
							askingAmount - 1 <= minContract ||
							payroll + askingAmount - 1 <= salaryCap;
						if (!capLegal) {
							continue;
						}
						const space = salaryCap - payroll;
						if (space > bestSpace) {
							bestSpace = space;
							bestTid = t.tid;
						}
					}
					if (bestTid !== undefined) {
						const years = helpers.bound(
							p.contract.exp - season > 0 ? p.contract.exp - season : 2,
							g.get("minContractLength"),
							g.get("maxContractLength"),
						);
						signings.push({
							pid: p.pid,
							tid: bestTid,
							amount: askingAmount,
							exp: season + years,
							name: `${p.firstName} ${p.lastName}`,
						});
						details.push({
							pid: p.pid,
							name: `${p.firstName} ${p.lastName}`,
							outcome: "ai_signed",
							tid: bestTid,
							amount: askingAmount,
						});
						payrolls.set(
							bestTid,
							(await getTeamPayroll(bestTid)) + askingAmount,
						);
						aiRosterCount.set(bestTid, (aiRosterCount.get(bestTid) ?? 0) + 1);
						// Record the losing human bids so they see they were outbid
						for (const bid of playerBids) {
							details.push({
								pid: p.pid,
								name: `${p.firstName} ${p.lastName}`,
								outcome: "outbid",
								tid: bid.tid,
								amount: bid.amount,
							});
						}
					}
				}
			} else if (signed) {
				for (const bid of playerBids) {
					if (!signings.some((s) => s.pid === p.pid && s.tid === bid.tid)) {
						details.push({
							pid: p.pid,
							name: `${p.firstName} ${p.lastName}`,
							outcome: "outbid",
							tid: bid.tid,
							amount: bid.amount,
						});
					}
				}
			}
		}
	}

	const event: FaRoundEventPayload = {
		round,
		phase: g.get("phase"),
		season: g.get("season"),
		signings,
		final,
	};

	const { data, error } = await getClient().rpc("resolve_fa_round", {
		p_league_id: leagueId,
		p_round: round,
		p_event: event,
		p_ledger_adds: signings.map((s) => ({
			kind: "player",
			id: s.pid,
			tid: s.tid,
		})),
		p_final: final,
	});
	if (error) {
		throw new Error(error.message);
	}
	if (data.status !== "resolved") {
		return data;
	}

	// Apply our own event from the log, like everyone else
	await syncNow(leagueId);

	return { status: "resolved", signings: signings.length, final, details };
};

// Auto-resolve when everyone has readied up ("done bidding") during an open
// auction. Called from the realtime members handler and after set_ready.
let autoResolving = false;
export const maybeAutoResolveFa = async (leagueId: string) => {
	if (autoResolving) {
		return;
	}
	autoResolving = true;
	try {
		const lid = g.get("lid");
		if (
			typeof lid !== "number" ||
			(await getLeagueIdForLid(lid)) !== leagueId
		) {
			return;
		}
		const { league: mpLeague, members } = await getLobbyState(leagueId);
		const session = await getSession();
		if (
			!mpLeague.fa_open ||
			!session ||
			session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
		) {
			return;
		}
		if (members.length === 0 || !members.every((m) => m.ready)) {
			return;
		}
		const result = await resolveFaRound(leagueId);
		if (result.status === "resolved") {
			logEvent({
				type: "info",
				text: result.final
					? "Free agency is over - no bids were submitted this round. Ready up to resume the season!"
					: `Free agency round resolved: ${result.signings} player(s) signed. Next round is open!`,
				saveToDb: false,
				showNotification: true,
			});
		}
	} catch (error) {
		logEvent({
			type: "error",
			text: `Resolving the free agency round failed: ${error.message}`,
			saveToDb: false,
		});
	} finally {
		autoResolving = false;
	}
};
